import { AfterViewInit, Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { ConfigProvider } from "../../../config-provider.service";
import { TranslateModule } from "@ngx-translate/core";
import { NgIf, NgOptimizedImage } from "@angular/common";
import { MatError, MatFormField, MatHint } from "@angular/material/form-field";
import { MatIcon } from "@angular/material/icon";
import { MatTooltip } from "@angular/material/tooltip";
import { MatInput } from "@angular/material/input";
import { MatIconButton } from "@angular/material/button";

@Component({
  selector: 'input-file',
  styleUrls: ['./input-file.scss'],
  standalone: true,
  imports: [
    TranslateModule,
    NgIf,
    MatHint,
    MatFormField,
    MatIcon,
    MatTooltip,
    ReactiveFormsModule,
    MatError,
    MatInput,
    MatIconButton,
    NgOptimizedImage
  ],
  templateUrl: './input-file.html'
})
export class InputFileComponent implements AfterViewInit {
  @Input() public set disabled(disabled: boolean) {
    this._disabled = disabled;
    if (this._disabled) {
      this.fileFormControl.disable();
    } else {
      this.fileFormControl.enable();
    }
  }
  public get disabled() {
    return this._disabled;
  }
  public ERROR_EXTENSION = 1;
  public ERROR_SIZE = 2;
  @ViewChild('fileInput', {static: false}) public fileInput:any
  @ViewChild('textInput', {static: false}) public textInput :any;

  public inputFile: string | null = null;
  public errorCode = 0;
  public isDragging = false;

  @Input() public fileData: FileData = { current: null, new: null };
  @Output() public fileDataChange: EventEmitter<FileData> = new EventEmitter<FileData>();
  @Output() public onFileDelete: EventEmitter<void> = new EventEmitter<void>();
  @Input() public placeholder: | null = null;
  @Input() public hint: string | null = null;
  @Input() public previewImage: string | null = null;
  @Input() public fileHost = '';
  @Input() public color: 'accent' | 'primary' | 'warn' = 'primary';
  @Input() public allowedExtensions: string[] = []; // tableau vide = toutes les extensions ...
  @Input() public maxFileSize = 0; // in Mo, 0 or less = unlimited

  public fileFormControl: FormControl = new FormControl('', [
    // doc : https://blog.thoughtram.io/angular/2016/03/14/custom-validators-in-angular-2.html#building-a-custom-validator
    (control: any) => this.errorCode !== this.ERROR_EXTENSION ? null : { extension: { valid: false } },
    (control: any) => this.errorCode === this.ERROR_SIZE ? { size: { valid: false } } : null
  ]);
  private _disabled = false;
  constructor(
    public conf: ConfigProvider
  ) { }

  public ngAfterViewInit() {

  }



  public onFileDragOver(event: any): void {
    event.preventDefault();
    this.isDragging = true;
  }

  public onFileDragLeave(event: any): void {
    event.preventDefault();
    this.isDragging = false;
  }

  public onFileDrop(event: any): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;

    if (this.disabled) {
      return;
    }

    const files: FileList = event.dataTransfer.files;
    if (files.length) {
      this.selectFile(files[0]);
    }

  }


  public onFocus(): void {
    if (this.disabled) {
      return;
    }

    this.openFileBrowser();
  }

  public onTextChange(): void {
    if (this.disabled) {
      return;
    }

    this.openFileBrowser();
    this.fileFormControl.setValue('');
  }

  public onRemoveFileClicked(event:any): void {
    event.stopPropagation()
    event.preventDefault();
    if (this.disabled) {
      return;
    }

    this.removeFile();
  }

  public onFileSelectionChange(event:any): void {
    if (this.disabled) {
      return;
    }

    const files: FileList = event.target.files;
    if (files.length) {
      this.selectFile(files[0]);
    }
  }

  public getSelectedFile(): File {
    return this.fileData.new;
  }

  public openFileBrowser(): void {
    this.fileInput.nativeElement.value = '';
    this.fileInput.nativeElement.click();
  }

  private selectFile(file: File): void {
    const fileExtension: string = this.getFileExtension(file);
    if (this.allowedExtensions.length > 0 && this.allowedExtensions.indexOf(fileExtension.toLowerCase()) < 0) {
      this.onError(this.ERROR_EXTENSION, fileExtension);
      return;
    }

    const fileSize: number = file.size / 1048576; // converson o => Mo
    if (this.maxFileSize && fileSize > this.maxFileSize) {
      this.onError(this.ERROR_SIZE, fileSize);
      return;
    }

    this.errorCode = 0;
    this.fileData.new = file;
    /*
    //TODO s'il n'y a pas de preview, ca sert � rien de lire le fichier ...
    let reader: FileReader = new FileReader();
    reader.onload = () => {
        this.inputFile = reader.result;
    };
    reader.readAsDataURL(file);
    */
    this.fileFormControl.updateValueAndValidity();
    this.fileDataChange.emit(this.fileData);
  }

  private removeFile(): void {
    this.fileData.new = null;
    this.fileData.current = null;
    this.inputFile = null;
    this.fileDataChange.emit(this.fileData);
    this.onFileDelete.emit();
  }

  private getFileExtension(file: File): string {
    const parts: string[] = file.name.split('.');

    return parts[parts.length - 1];
  }

  private onError(errorCode: number, extra: any): void {
    this.errorCode = errorCode;
    this.textInput.nativeElement.blur();
    this.fileFormControl.updateValueAndValidity();
  }
}

export interface FileData {
  current: string | null;
  // on peut pas explicitement mettre un type File parce que
  // sinon on peut pas attribuer cette valeur � un model
  // dont le champs file est d�clar� en type string
  // new: File;
  new: any;
}
